import React from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import {
  CheckCircle,
  RadioButtonChecked,
  RadioButtonUnchecked,
  Celebration,
  AccountBalance,
  FlashOn,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../login/brand';
import { DASH, flowColor } from './dashboardColors';

const stageKeys = {
  fastFunding: [
    { key: 'application', icon: '📝' },
    { key: 'documents', icon: '📁' },
    { key: 'analysis', icon: '🔍' },
    { key: 'approved', icon: '✅' },
    { key: 'closing', icon: '📋' },
    { key: 'funded', icon: '🎉' },
  ],
  sba: [
    { key: 'pfs', icon: '📊' },
    { key: 'documents', icon: '📁' },
    { key: 'analysis', icon: '🔍' },
    { key: 'approved', icon: '✅' },
    { key: 'closing', icon: '📋' },
    { key: 'funded', icon: '🎉' },
  ],
};

const FundingPipeline = ({ 
  type = 'fastFunding', // 'fastFunding' or 'sba'
  currentStage = 1, // 1-6 (1 = first step completed)
  compact = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const pipelineStages = stageKeys[type];
  const isSBA = type === 'sba';
  
  const primaryColor = flowColor(type);
  const gradientStart = isSBA ? DASH.brown : BRAND.copperDark;
  const gradientEnd = isSBA ? BRAND.copperDark : BRAND.copperLight;

  const getStageStatus = (index) => {
    if (index < currentStage) return 'completed';
    if (index === currentStage) return 'current';
    return 'pending';
  };

  const getStageIcon = (index, stage) => {
    const status = getStageStatus(index);
    
    if (index === pipelineStages.length - 1 && status === 'completed') {
      return <Celebration sx={{ fontSize: compact ? 18 : 24, color: BRAND.copperLight }} />;
    }
    
    if (status === 'completed') {
      return <CheckCircle sx={{ fontSize: compact ? 18 : 24, color: primaryColor }} />;
    }
    
    if (status === 'current') {
      return (
        <RadioButtonChecked 
          sx={{ 
            fontSize: compact ? 18 : 24, 
            color: primaryColor,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.5 },
              '100%': { opacity: 1 },
            },
          }} 
        />
      );
    }
    
    return <RadioButtonUnchecked sx={{ fontSize: compact ? 18 : 24, color: 'grey.400' }} />;
  };

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(gradientStart, 0.05)} 0%, ${alpha(gradientEnd, 0.1)} 100%)`,
        borderRadius: 3,
        p: compact ? 2 : 3,
        border: `1px solid ${alpha(primaryColor, 0.2)}`,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: compact ? 2 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isSBA
            ? <AccountBalance sx={{ fontSize: compact ? 18 : 22, color: primaryColor }} />
            : <FlashOn sx={{ fontSize: compact ? 18 : 22, color: primaryColor }} />}
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: compact ? '1rem' : '1.125rem', color: DASH.ink }}>
            {isSBA ? t('dashboard.sbaLoanPipeline') : t('dashboard.fastFundingPipeline')}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 2,
            py: 0.5,
            borderRadius: 2,
            bgcolor: alpha(primaryColor, 0.1),
            border: `1px solid ${alpha(primaryColor, 0.3)}`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: primaryColor }}>
            {t('dashboard.stage')} {currentStage}/{pipelineStages.length}
          </Typography>
        </Box>
      </Box>

      {/* Pipeline */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          position: 'relative',
          px: compact ? 0.5 : 1,
          overflowX: 'auto',
          overflowY: 'hidden',
          mx: -1,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {/* Connection Line */}
        <Box
          sx={{
            position: 'absolute',
            top: compact ? 16 : 20,
            left: compact ? 30 : 40,
            right: compact ? 30 : 40,
            height: 4,
            bgcolor: 'grey.200',
            borderRadius: 2,
            zIndex: 0,
          }}
        />
        {/* Progress Line */}
        <Box
          sx={{
            position: 'absolute',
            top: compact ? 16 : 20,
            left: compact ? 30 : 40,
            width: `calc(${((currentStage - 1) / (pipelineStages.length - 1)) * 100}% - ${compact ? 60 : 80}px * ${(currentStage - 1) / (pipelineStages.length - 1)})`,
            height: 4,
            background: `linear-gradient(90deg, ${gradientStart}, ${gradientEnd})`,
            borderRadius: 2,
            zIndex: 1,
            transition: 'width 0.5s ease-in-out',
          }}
        />

        {/* Stages */}
        {pipelineStages.map((stage, index) => {
          const status = getStageStatus(index);
          
          return (
            <Box
              key={stage.key}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                zIndex: 2,
                flex: '1 1 0',
                minWidth: compact ? 40 : 48,
              }}
            >
              {/* Icon Circle */}
              <Box
                sx={{
                  width: compact ? 32 : 42,
                  height: compact ? 32 : 42,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: status === 'pending' ? 'grey.100' : alpha(primaryColor, 0.1),
                  border: status === 'current' ? `2px solid ${primaryColor}` : 'none',
                  boxShadow: status === 'current' ? `0 0 0 3px ${alpha(primaryColor, 0.2)}` : 'none',
                  transition: 'all 0.3s ease',
                }}
              >
                {getStageIcon(index, stage)}
              </Box>
              
              {/* Label */}
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  fontWeight: status === 'current' ? 700 : 500,
                  color: status === 'pending' ? 'text.secondary' : 'text.primary',
                  fontSize: compact ? '0.6rem' : '0.65rem',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                {t(`dashboard.${stage.key}`)}
              </Typography>
              
              {/* Current indicator */}
              {status === 'current' && !compact && (
                <Typography
                  variant="caption"
                  sx={{
                    color: primaryColor,
                    fontWeight: 600,
                    fontSize: '0.55rem',
                    mt: 0.25,
                    lineHeight: 1.2,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('dashboard.youreHere')}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default FundingPipeline;
